import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import { NumberInput } from "minimal-ui";
import { Iconify } from "../Minimal/src/components/iconify/iconify";
import { getPromotionGetProducts, resolveCartLinePromotionId, resolveCartLineSlabId, changeGetProduct } from "../../data/promotions";
import { getCartList } from "../../data/cart";
import { getCompanySettings, resolveDisplayProductStock } from "../../utils/companySettingsCache";
import {
  resolveLineOrderType,
  buildExistingCartQuantities,
  calculateMaxAllowedGetQuantity,
  computeQtyBounds,
  toValidLimit,
  parsePromotionMinTotalGetQty,
  buildAllNewGetProductsPayload,
  buildRemovedGetProductsPayload,
  resolveRemovedQty,
  stripRemovedGetLinesFromCart,
  resolveOfferProductOrderType,
  resolveOfferProductStockQty,
  resolveMandatoryGetMinQuantity,
  resolvePromotionGetProductQtyInCart,
  isCompulsoryOfferProduct,
} from "../sales/Promotion/utils/promotionOfferProductsShared";
import "./commonStyles.css";

const sortCompulsoryGetProductsFirst = (list) => {
  if (!Array.isArray(list) || list.length < 2) {
    return Array.isArray(list) ? list : [];
  }
  return [...list].sort((a, b) => {
    const aComp = isCompulsoryOfferProduct(a) ? 1 : 0;
    const bComp = isCompulsoryOfferProduct(b) ? 1 : 0;
    return bComp - aComp;
  });
};

const isCompulsoryGetRow = (row) => isCompulsoryOfferProduct(row);

const resolveOfferProductGroupId = (product) => {
  const g =
    product?.group_id ??
    product?.group_no ??
    product?.promoGroupId ??
    product?.promo_group_id;
  if (g === null || g === undefined) return null;
  const s = String(g).trim();
  return s === "" ? null : s;
};

const sortGroupIds = (ids) =>
  [...ids].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (
      !Number.isNaN(na) &&
      !Number.isNaN(nb) &&
      String(na) === String(a) &&
      String(nb) === String(b)
    ) {
      return na - nb;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  });

/** Map product_ids from API `groups` onto get_products when group_id is missing. */
const enrichGetProductsWithGroups = (products, groups) => {
  if (!Array.isArray(products)) return [];
  if (!Array.isArray(groups) || groups.length === 0) return products;

  const pidToGroup = new Map();
  const groupQtyById = new Map();
  groups.forEach((g) => {
    const gid = String(g?.group_id ?? "").trim();
    if (!gid) return;
    const qty = g?.group_quantity ?? g?.quantity;
    if (qty !== undefined && qty !== null && String(qty).trim() !== "") {
      groupQtyById.set(gid, String(qty));
    }
    (Array.isArray(g?.product_ids) ? g.product_ids : []).forEach((pid) => {
      const id = String(pid ?? "").trim();
      if (id) pidToGroup.set(id, gid);
    });
  });

  return products.map((p) => {
    const pid = String(p?.product_id ?? "").trim();
    const existingGid = resolveOfferProductGroupId(p);
    const gid = existingGid || (pid ? pidToGroup.get(pid) : null) || null;
    if (!gid) return p;
    const groupQty =
      p?.group_quantity ??
      groupQtyById.get(gid) ??
      p?.quantity ??
      null;
    return {
      ...p,
      group_id: gid,
      promoGroupId: gid,
      ...(groupQty != null ? { group_quantity: String(groupQty) } : {}),
    };
  });
};

const resolveGetProductGroupIds = (products, groups) => {
  if (Array.isArray(groups) && groups.length > 0) {
    const seen = new Set();
    const ordered = [];
    groups.forEach((g) => {
      const gid = String(g?.group_id ?? "").trim();
      if (!gid || seen.has(gid)) return;
      seen.add(gid);
      ordered.push(gid);
    });
    if (ordered.length > 0) return sortGroupIds(ordered);
  }

  const seen = new Set();
  const ordered = [];
  (Array.isArray(products) ? products : []).forEach((p) => {
    const gid = resolveOfferProductGroupId(p);
    if (gid !== null && !seen.has(gid)) {
      seen.add(gid);
      ordered.push(gid);
    }
  });
  return sortGroupIds(ordered);
};

const PromotionGetProductsDialog = ({
  open,
  onClose,
  cartItem = null,
  promotionId = 27, // fallback when cart line has no promo_id (ws-cart-list)
  slabId: slabIdProp = null,
  channels = 5,
  platform = "5",
  customerId = null,
  orderId = null,
  onSwapSuccess = null,
  onCartUpdate = null,
  onQueuePromotionCheck = null,
  /** Edit order: swap via check-promotion `changed_get_products` instead of change_get_product API. */
  onCheckPromotionGetSwap = null,
  localCartItems = undefined,
  bundleDiscounts = [],
}) => {
  const [rows, setRows] = useState([]);
  const [promoDetails, setPromoDetails] = useState(null);
  const [existingQuantities, setExistingQuantities] = useState({});
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedGetGroupId, setSelectedGetGroupId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [messagePopup, setMessagePopup] = useState({
    open: false,
    message: "",
    severity: "error",
    title: "",
  });
  const [shouldEnforceStockLimit, setShouldEnforceStockLimit] = useState(true);
  const tableWrapRef = useRef(null);
  const dialogPaperRef = useRef(null);
  const returnFocusRef = useRef(null);
  const handleKeyDownRef = useRef(null);
  const handleConfirmSwapRef = useRef(() => { });

  /** Prefer `promo_id` from ws-cart-list cart line; then `promotionId` prop. */
  const resolvedPromoId = useMemo(() => {
    const fromCart = resolveCartLinePromotionId(cartItem);
    if (fromCart) return fromCart;
    if (promotionId === undefined || promotionId === null || promotionId === "") {
      return null;
    }
    const s = String(promotionId).trim();
    return s === "" || s === "0" ? null : s;
  }, [cartItem, promotionId]);

  const resolvedSlabId = useMemo(() => {
    if (slabIdProp !== undefined && slabIdProp !== null && String(slabIdProp).trim() !== "") {
      const s = String(slabIdProp).trim();
      return s === "0" ? null : s;
    }
    return resolveCartLineSlabId(
      cartItem,
      bundleDiscounts,
      Array.isArray(localCartItems) ? localCartItems : []
    );
  }, [cartItem, slabIdProp, bundleDiscounts, localCartItems]);

  const cartItemsForBounds = useMemo(
    () => (Array.isArray(localCartItems) ? localCartItems : []),
    [localCartItems]
  );

  const getProductGroupIds = useMemo(
    () => resolveGetProductGroupIds(rows, promoDetails?.groups),
    [rows, promoDetails?.groups]
  );

  const filteredRows = useMemo(() => {
    if (getProductGroupIds.length > 1 && selectedGetGroupId != null) {
      return rows.filter(
        (p) => String(resolveOfferProductGroupId(p)) === String(selectedGetGroupId)
      );
    }
    return rows;
  }, [rows, getProductGroupIds, selectedGetGroupId]);

  const selectedGroupQuantity = useMemo(() => {
    if (selectedGetGroupId == null) return null;
    const fromGroups = (Array.isArray(promoDetails?.groups) ? promoDetails.groups : []).find(
      (g) => String(g?.group_id) === String(selectedGetGroupId)
    );
    if (fromGroups) {
      const q = parseInt(String(fromGroups.group_quantity ?? fromGroups.quantity ?? ""), 10);
      if (Number.isFinite(q) && q > 0) return q;
    }
    for (const p of filteredRows) {
      const q = parseInt(String(p?.group_quantity ?? p?.quantity ?? ""), 10);
      if (Number.isFinite(q) && q > 0) return q;
    }
    return null;
  }, [selectedGetGroupId, promoDetails?.groups, filteredRows]);

  const selectedGroupFilledQty = useMemo(() => {
    if (!filteredRows.length) return 0;
    return filteredRows.reduce((sum, row) => {
      const pid = String(row.product_id);
      return sum + Math.max(0, Math.floor(Number(quantities[pid] ?? 0) || 0));
    }, 0);
  }, [filteredRows, quantities]);

  useEffect(() => {
    if (!open) return;
    if (getProductGroupIds.length === 0) {
      setSelectedGetGroupId(null);
      return;
    }
    setSelectedGetGroupId((prev) =>
      prev != null && getProductGroupIds.some((id) => String(id) === String(prev))
        ? prev
        : getProductGroupIds[0]
    );
  }, [open, resolvedPromoId, getProductGroupIds]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [selectedGetGroupId]);

  const showMaxQtyError = useCallback(() => {
    setMessagePopup({
      open: true,
      message: "Maximum quantity reached.",
      severity: "error",
      title: "Maximum quantity reached",
    });
  }, []);

  const showCompulsoryMinQtyError = useCallback((compulsoryQty) => {
    setMessagePopup({
      open: true,
      message: `Minimum quantity for this compulsory product is ${compulsoryQty}`,
      severity: "warning",
      title: "Compulsory product",
    });
  }, []);

  const showCompulsoryRemoveError = useCallback(() => {
    setMessagePopup({
      open: true,
      message: "This is a compulsory product and cannot be removed",
      severity: "warning",
      title: "Compulsory product",
    });
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    requestAnimationFrame(() => {
      const el = returnFocusRef.current;
      if (el && typeof el.focus === "function") {
        try {
          el.focus({ preventScroll: true });
        } catch {
          /* ignore focus errors */
        }
      }
      returnFocusRef.current = null;
    });
  }, [onClose]);

  const focusQtyInputForRow = useCallback((idx) => {
    const container = tableWrapRef.current;
    if (!container) return false;
    const tr = container.querySelector(`tbody tr[data-row-index="${idx}"]`);
    if (!tr) return false;
    const wrap = tr.querySelector("[data-offer-qty-focus]");
    const root = wrap || tr;
    const focusable =
      root.querySelector("input:not([type='hidden'])") ||
      root.querySelector("button:not([disabled])");
    if (focusable && typeof focusable.focus === "function") {
      focusable.focus({ preventScroll: true });
      return true;
    }
    return false;
  }, []);

  const focusDialogShell = useCallback(() => {
    const paper = dialogPaperRef.current;
    if (paper && typeof paper.focus === "function") {
      paper.focus({ preventScroll: true });
      return true;
    }
    return false;
  }, []);

  const focusFirstFocusableInDialog = useCallback(() => {
    const root = dialogPaperRef.current;
    if (!root) return false;
    const selector =
      'button:not([disabled]), input:not([disabled]):not([type="hidden"]), [href], [tabindex]:not([tabindex="-1"])';
    const first = root.querySelector(selector);
    if (first && typeof first.focus === "function") {
      first.focus({ preventScroll: true });
      return true;
    }
    return focusDialogShell();
  }, [focusDialogShell]);

  const focusFirstInteractive = useCallback(() => {
    if (!filteredRows.length || loading || loadError || messagePopup.open) return;
    setSelectedIndex(0);
    focusQtyInputForRow(0);
  }, [filteredRows.length, loading, loadError, messagePopup.open, focusQtyInputForRow]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      returnFocusRef.current = active;
    }
    focusDialogShell();
    const rafId = requestAnimationFrame(() => {
      focusDialogShell();
    });
    const timerId = window.setTimeout(() => {
      focusDialogShell();
    }, 0);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timerId);
    };
  }, [open, focusDialogShell]);

  useEffect(() => {
    if (!open || loading || loadError || !filteredRows.length || messagePopup.open) {
      return undefined;
    }
    const id = requestAnimationFrame(() => {
      focusFirstInteractive();
    });
    return () => cancelAnimationFrame(id);
  }, [
    open,
    loading,
    loadError,
    filteredRows.length,
    messagePopup.open,
    focusFirstInteractive,
  ]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onWindowKeyDown = (e) => {
      const root =
        dialogPaperRef.current ||
        document.querySelector('[data-offer-products-dialog="true"]');
      if (!root) return;

      const target = e.target;
      const active = document.activeElement;
      const inDialog =
        (target instanceof Element && root.contains(target)) ||
        (active instanceof Element && root.contains(active));

      if (e.key === "Tab" && !inDialog) {
        e.preventDefault();
        e.stopImmediatePropagation();
        focusFirstFocusableInDialog();
        return;
      }

      const isNavKey =
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight";

      if (isNavKey && !inDialog) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setSelectedIndex(0);
        focusQtyInputForRow(0);
        return;
      }

      if (!inDialog) return;
      handleKeyDownRef.current?.(e);
    };
    window.addEventListener("keydown", onWindowKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onWindowKeyDown, true);
    };
  }, [open, focusQtyInputForRow, focusFirstFocusableInDialog, focusDialogShell]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const settings = await getCompanySettings();
      if (!cancelled) {
        setShouldEnforceStockLimit(resolveDisplayProductStock(settings));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !resolvedPromoId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setRows([]);
    setPromoDetails(null);
    setExistingQuantities({});
    setQuantities({});
    setSelectedIndex(0);
    setSelectedGetGroupId(null);
    (async () => {
      try {
        const res = await getPromotionGetProducts(resolvedPromoId, {
          channels,
          slabId: resolvedSlabId,
        });
        if (cancelled) return;
        const data = res?.data || res || null;
        setPromoDetails(data || null);
        const rawList = (data?.get_products || data?.offer_products || []).filter(
          (p) => p && (p.product_name || "").toString().trim() !== ""
        );
        const list = sortCompulsoryGetProductsFirst(
          enrichGetProductsWithGroups(rawList, data?.groups)
        );
        setRows(list);
        if (!list.length) {
          setLoadError("No products available for this promotion");
        } else {
          setLoadError("");
        }
        setLoading(false);

        const useLocalCart =
          localCartItems !== undefined && localCartItems !== null;
        let cartRows = null;
        if (useLocalCart) {
          cartRows = Array.isArray(localCartItems) ? localCartItems : [];
        } else if (customerId && platform) {
          const cartRes = await getCartList(customerId, platform).catch(
            () => null
          );
          if (cancelled) return;
          cartRows = Array.isArray(cartRes?.data) ? cartRes.data : null;
        }
        if (data && cartRows) {
          const useEposOfferLogic = !!(orderId || onCheckPromotionGetSwap);
          setExistingQuantities(
            buildExistingCartQuantities(cartRows, data, String(resolvedPromoId), {
              eposOfferProductLogic: useEposOfferLogic,
            })
          );
        } else {
          setExistingQuantities({});
        }
      } catch {
        if (!cancelled) {
          setLoadError("Failed to load promotion products");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, resolvedPromoId, resolvedSlabId, channels, customerId, platform, localCartItems, orderId, onCheckPromotionGetSwap]);

  const orderTypeForDisplay = cartItem ? resolveLineOrderType(cartItem) : "Box";

  useEffect(() => {
    if (!rows.length) return;
    const next = {};
    rows.forEach((p) => {
      const pid = String(p.product_id);
      const prefix = `get_${pid}_`;
      let existingCartQty = 0;
      Object.keys(existingQuantities).forEach((key) => {
        if (key.startsWith(prefix)) {
          existingCartQty += existingQuantities[key] || 0;
        }
      });
      next[pid] = existingCartQty;
    });
    if (cartItem) {
      const cartPid = String(cartItem.product_id ?? "");
      const cartQty = Math.floor(resolveRemovedQty(cartItem));
      if (cartPid && cartQty > 0 && rows.some((p) => String(p.product_id) === cartPid)) {
        next[cartPid] = Math.max(next[cartPid] || 0, cartQty);
      }
    }
    if (cartItemsForBounds.length > 0 && resolvedPromoId && promoDetails) {
      const promoIdStr = String(resolvedPromoId);
      rows.forEach((p) => {
        const pid = String(p.product_id);
        const fromCart = resolvePromotionGetProductQtyInCart(
          cartItemsForBounds,
          pid,
          promoDetails,
          promoIdStr,
          cartItem
        );
        if (fromCart > 0) {
          next[pid] = Math.max(next[pid] || 0, fromCart);
        }
      });
    }
    if (promoDetails) {
      rows.forEach((p) => {
        if (!isCompulsoryGetRow(p)) return;
        const pid = String(p.product_id);
        const mandatoryMin = resolveMandatoryGetMinQuantity(
          p,
          promoDetails,
          existingQuantities,
          next,
          {
            cartItems: cartItemsForBounds.length ? cartItemsForBounds : null,
            promotionIdStr: resolvedPromoId || "",
            referenceCartItem: cartItem,
            bundleDiscounts,
          }
        );
        const floor = Math.max(1, mandatoryMin);
        if ((next[pid] || 0) < floor) {
          next[pid] = floor;
        }
      });
    }
    setQuantities(next);
  }, [rows, existingQuantities, cartItem, cartItemsForBounds, resolvedPromoId, promoDetails, bundleDiscounts]);

  const scrollSelectedIntoView = useCallback((idx) => {
    const container = tableWrapRef.current;
    if (!container) return;
    const tr = container.querySelector(`tbody tr[data-row-index="${idx}"]`);
    if (tr && typeof tr.scrollIntoView === "function") {
      tr.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, []);

  const scrollListByPage = useCallback((direction) => {
    const el = tableWrapRef.current;
    if (!el) return;
    const page = Math.max(el.clientHeight * 0.85, 80);
    const next = el.scrollTop + direction * page;
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    el.scrollTop = Math.min(maxScroll, Math.max(0, next));
  }, []);

  const moveSelection = (delta) => {
    if (!filteredRows.length) return;
    setSelectedIndex((prev) => {
      const next = Math.min(filteredRows.length - 1, Math.max(0, prev + delta));
      setTimeout(() => {
        scrollSelectedIntoView(next);
        focusQtyInputForRow(next);
      }, 0);
      return next;
    });
  };

  const getMandatoryGetMinForRow = useCallback(
    (row) => {
      if (!promoDetails || !row) return 0;
      const computed = resolveMandatoryGetMinQuantity(
        row,
        promoDetails,
        existingQuantities,
        quantities,
        {
          cartItems: cartItemsForBounds.length ? cartItemsForBounds : null,
          promotionIdStr: resolvedPromoId || "",
          referenceCartItem: cartItem,
          bundleDiscounts,
        }
      );
      if (computed <= 0) return 0;
      return computed;
    },
    [promoDetails, existingQuantities, quantities, cartItemsForBounds, resolvedPromoId, cartItem, bundleDiscounts]
  );

  const getRowBounds = useCallback(
    (product) => {
      if (!promoDetails || !cartItem || !product) {
        return { minQ: 1, maxQ: 9999, maxAllowedGet: 0, totalGet: 0, removedQty: 0, stockMax: null };
      }
      const base = computeQtyBounds({
        promoDetails,
        existingQuantities,
        cartItem,
        selectedProduct: product,
        orderType: resolveOfferProductOrderType(product, orderTypeForDisplay),
        uiQuantities: quantities,
        skipOfferStockLimit: !shouldEnforceStockLimit,
        cartItems: cartItemsForBounds.length ? cartItemsForBounds : null,
        promotionIdStr: resolvedPromoId || "",
        referenceCartItem: cartItem,
        bundleDiscounts,
      });
      const maxOrder = toValidLimit(product?.maximumOrderQuantity ?? product?.maximum_order_quantity);
      let maxQ = base.maxQ;
      if (maxOrder != null) {
        maxQ = Math.min(maxQ, maxOrder);
      }
      return { ...base, maxQ };
    },
    [promoDetails, existingQuantities, cartItem, orderTypeForDisplay, quantities, shouldEnforceStockLimit, cartItemsForBounds, resolvedPromoId, bundleDiscounts]
  );

  const getRowStaticCap = useCallback(
    (product) => {
      const bounds = getRowBounds(product);
      const maxOrder = toValidLimit(
        product?.maximumOrderQuantity ?? product?.maximum_order_quantity
      );
      let cap = Number.POSITIVE_INFINITY;
      if (bounds.stockMax != null) {
        cap = Math.min(cap, Math.max(0, Math.floor(bounds.stockMax)));
      }
      if (maxOrder != null) {
        cap = Math.min(cap, maxOrder);
      }
      if (!Number.isFinite(cap)) {
        return 9999;
      }
      return Math.max(0, Math.floor(cap));
    },
    [getRowBounds]
  );

  const getPromotionGetCap = useCallback(() => {
    if (!promoDetails) return 0;
    return Math.max(
      0,
      Math.floor(calculateMaxAllowedGetQuantity(promoDetails, existingQuantities))
    );
  }, [promoDetails, existingQuantities]);

  const applyQuantityWithRebalance = useCallback(
    (row, requestedQty, prev) => {
      const pid = String(row.product_id);
      const normalizedRequested = Math.max(
        0,
        Math.floor(Number(requestedQty) || 0)
      );
      const staticCap = getRowStaticCap(row);
      const promotionCap = getPromotionGetCap();
      const next = { ...prev };
      const compulsoryFloor = isCompulsoryGetRow(row)
        ? Math.max(1, getMandatoryGetMinForRow(row))
        : 0;

      if (promotionCap <= 0) {
        return { next: prev, reachedLimit: normalizedRequested > compulsoryFloor };
      }

      if (compulsoryFloor > 0 && normalizedRequested < compulsoryFloor) {
        return { next: prev, reachedLimit: false };
      }

      if (normalizedRequested <= 0) {
        next[pid] = 0;
        return { next, reachedLimit: false };
      }

      const totalOthers = Object.entries(prev).reduce((sum, [k, v]) => {
        if (k === pid) return sum;
        return sum + Math.max(0, Math.floor(Number(v) || 0));
      }, 0);

      const maxForThisRow = Math.min(
        staticCap,
        Math.max(0, promotionCap - totalOthers)
      );
      let finalQty = Math.min(normalizedRequested, maxForThisRow);
      if (compulsoryFloor > 0 && finalQty < compulsoryFloor) {
        return { next: prev, reachedLimit: normalizedRequested > maxForThisRow };
      }
      next[pid] = finalQty;
      return { next, reachedLimit: finalQty < normalizedRequested };
    },
    [getRowStaticCap, getPromotionGetCap, getMandatoryGetMinForRow]
  );

  const adjustQuantityForRow = useCallback(
    (rowIndex, delta) => {
      if (!filteredRows.length) return;
      const row = filteredRows[rowIndex];
      if (!row) return;
      setSelectedIndex(rowIndex);
      const pid = String(row.product_id);
      const mandatoryMin = getMandatoryGetMinForRow(row);
      let shouldWarnMax = false;
      setQuantities((prev) => {
        const rawVal = prev[pid] ?? 0;
        if (delta < 0 && rawVal <= 0) {
          return prev;
        }
        let numValue = Math.floor(Number(rawVal)) + delta;
        numValue = Math.max(numValue, 0);
        const floor = isCompulsoryGetRow(row)
          ? Math.max(1, mandatoryMin)
          : 0;
        if (floor > 0) {
          if (numValue < floor && rawVal > 0) {
            if (numValue <= 0) {
              showCompulsoryRemoveError();
            } else {
              showCompulsoryMinQtyError(floor);
            }
            return prev;
          }
        }
        const { next, reachedLimit } = applyQuantityWithRebalance(
          row,
          numValue,
          prev
        );
        shouldWarnMax = reachedLimit;
        return next;
      });
      if (shouldWarnMax) {
        showMaxQtyError();
      }
    },
    [
      filteredRows,
      showMaxQtyError,
      showCompulsoryMinQtyError,
      showCompulsoryRemoveError,
      applyQuantityWithRebalance,
      getMandatoryGetMinForRow,
    ]
  );

  const adjustSelectedQuantity = useCallback(
    (delta) => {
      adjustQuantityForRow(selectedIndex, delta);
    },
    [adjustQuantityForRow, selectedIndex]
  );

  const setSelectedQuantityToBound = useCallback(
    (edge) => {
      if (!filteredRows.length) return;
      const row = filteredRows[selectedIndex];
      if (!row) return;
      const pid = String(row.product_id);
      const bounds = getRowBounds(row);
      const mandatoryMin = getMandatoryGetMinForRow(row);
      setQuantities((prev) => {
        let qty = edge === "home" ? 0 : Math.max(0, Math.floor(bounds.maxQ));
        const floor = isCompulsoryGetRow(row)
          ? Math.max(1, mandatoryMin)
          : 0;
        if (edge === "home" && floor > 0) {
          showCompulsoryRemoveError();
          return prev;
        }
        if (
          edge === "end" &&
          floor > 0 &&
          qty > 0 &&
          qty < floor
        ) {
          qty = Math.min(floor, Math.max(0, Math.floor(bounds.maxQ)));
        }
        const { next } = applyQuantityWithRebalance(row, qty, prev);
        return next;
      });
    },
    [
      filteredRows,
      selectedIndex,
      getRowBounds,
      applyQuantityWithRebalance,
      getMandatoryGetMinForRow,
      showCompulsoryRemoveError,
    ]
  );

  const resolveSwapErrorMessage = useCallback((rawMessage) => {
    const cleaned = String(rawMessage || "").trim();
    if (cleaned.toLowerCase().includes("new_product_id is required")) {
      return "Please add promotional Products";
    }
    return cleaned || "Failed to swap promotional products";
  }, []);

  const handleConfirmSwap = async () => {
    if (!cartItem || !customerId || !rows.length || !promoDetails) {
      setMessagePopup({
        open: true,
        message: "Missing customer or product selection",
        severity: "error",
      });
      return;
    }
    if (!resolvedPromoId) {
      setMessagePopup({
        open: true,
        message: "Missing promotion id on cart line (promo_id).",
        severity: "error",
      });
      return;
    }
    const orderType = resolveLineOrderType(cartItem);

    const normalizedRequestedQuantities = {};
    let draftQuantities = rows.reduce((acc, row) => {
      const pid = String(row.product_id);
      acc[pid] = Math.max(0, Math.floor(Number(quantities[pid] ?? 0) || 0));
      return acc;
    }, {});

    for (const row of rows) {
      const pid = String(row.product_id);
      const requestedQty = Math.max(
        0,
        Math.floor(Number(draftQuantities[pid] ?? 0) || 0)
      );
      const mandatoryMin = getMandatoryGetMinForRow(row);
      const floor = isCompulsoryGetRow(row) ? Math.max(1, mandatoryMin) : 0;
      if (floor > 0 && requestedQty <= 0) {
        setMessagePopup({
          open: true,
          message: `${row.product_code || "This"} product is compulsory and cannot be removed.`,
          severity: "error",
        });
        return;
      }
      if (floor > 0 && requestedQty > 0 && requestedQty < floor) {
        setMessagePopup({
          open: true,
          message: `${row.product_code || "This"} product is compulsory with minimum quantity ${floor}.`,
          severity: "error",
        });
        return;
      }
      const staticCap = getRowStaticCap(row);
      if (requestedQty > staticCap) {
        setMessagePopup({
          open: true,
          message: `Quantity for ${row.product_name || row.product_code || "product"} cannot exceed ${staticCap}.`,
          severity: "error",
        });
        return;
      }
      const { next } = applyQuantityWithRebalance(row, requestedQty, draftQuantities);
      draftQuantities = next;
    }

    for (const row of rows) {
      const pid = String(row.product_id);
      const afterQty = Math.max(
        0,
        Math.floor(Number(draftQuantities[pid] ?? 0) || 0)
      );
      const afterMin = getMandatoryGetMinForRow(row);
      const afterFloor = isCompulsoryGetRow(row) ? Math.max(1, afterMin) : 0;
      if (afterFloor > 0 && afterQty <= 0) {
        setMessagePopup({
          open: true,
          message: `${row.product_code || "This"} product is compulsory and cannot be removed.`,
          severity: "error",
        });
        return;
      }
      if (afterFloor > 0 && afterQty < afterFloor) {
        setMessagePopup({
          open: true,
          message: `${row.product_code || "This"} product is compulsory with minimum quantity ${afterFloor}.`,
          severity: "error",
        });
        return;
      }
    }

    rows.forEach((row) => {
      const pid = String(row.product_id);
      normalizedRequestedQuantities[pid] = Math.max(
        0,
        Math.floor(Number(draftQuantities[pid] ?? 0) || 0)
      );
    });

    const minGetTotalRequired = parsePromotionMinTotalGetQty(promoDetails);
    const requestedTotalQty = rows.reduce(
      (sum, row) =>
        sum + (normalizedRequestedQuantities[String(row.product_id)] || 0),
      0
    );
    if (requestedTotalQty <= 0) {
      setMessagePopup({
        open: true,
        message: "Please set quantity for at least one offer product.",
        severity: "error",
      });
      return;
    }
    if (minGetTotalRequired > 0 && requestedTotalQty < minGetTotalRequired) {
      setMessagePopup({
        open: true,
        message: `This promotion needs at least ${minGetTotalRequired} total offer quantity.`,
        severity: "error",
      });
      return;
    }

    const promotionCap = getPromotionGetCap();
    if (requestedTotalQty > promotionCap) {
      setMessagePopup({
        open: true,
        message: `Total offer quantity cannot exceed ${promotionCap}.`,
        severity: "error",
      });
      return;
    }

    const removed = buildRemovedGetProductsPayload(
      existingQuantities,
      normalizedRequestedQuantities,
      { cartItem }
    );
    const newProducts = buildAllNewGetProductsPayload(
      filteredRows[selectedIndex] || filteredRows[0] || rows[0],
      orderType,
      0,
      existingQuantities,
      cartItem,
      rows,
      normalizedRequestedQuantities
    );
    if (!Array.isArray(newProducts) || newProducts.length === 0) {
      setMessagePopup({
        open: true,
        message: "Please add promotional Products",
        severity: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (typeof onCheckPromotionGetSwap === "function") {
        response = await onCheckPromotionGetSwap({
          promotionId: resolvedPromoId,
          removed,
          newProducts,
        });
      } else {
        response = await changeGetProduct(
          resolvedPromoId,
          removed,
          newProducts,
          platform,
          customerId,
          orderId
        );
      }

      if (response?.success) {
        if (typeof onCheckPromotionGetSwap !== "function") {
          const cartFromSwap = response?.data?.cart_items;
          const totalsFromSwap =
            response?.data?.totals ?? response?.data?.order_totals ?? null;
          const hasSwapCart =
            Array.isArray(cartFromSwap) && cartFromSwap.length > 0;

          if (hasSwapCart && onCartUpdate) {
            onCartUpdate(cartFromSwap, totalsFromSwap);
          } else if (onCartUpdate && Array.isArray(localCartItems)) {
            const strippedCart = stripRemovedGetLinesFromCart(
              localCartItems,
              removed
            );
            if (strippedCart.length !== localCartItems.length) {
              onCartUpdate(strippedCart, null);
            }
          }

          if (onQueuePromotionCheck) {
            onQueuePromotionCheck({ timestamp: Date.now() });
          }
        }

        if (onSwapSuccess) {
          onSwapSuccess();
        }
        handleClose();
      } else {
        let errorMsg = response?.message || response?.data?.message || response?.error || "Failed to swap promotional products";
        
        // Extract detailed error from compulsory_get_errors if available
        if (response?.data?.compulsory_get_errors && Array.isArray(response.data.compulsory_get_errors) && response.data.compulsory_get_errors.length > 0) {
          const compErrors = response.data.compulsory_get_errors;
          // Use the first error's message if available
          if (compErrors[0]?.message) {
            errorMsg = compErrors[0].message;
          }
        }
        
        const msg = resolveSwapErrorMessage(errorMsg);
        setMessagePopup({ open: true, message: msg, severity: "error" });
      }
    } catch (e) {
      const msg = resolveSwapErrorMessage(
        e?.message ||
        e?.response?.data?.message ||
        "Failed to swap promotional products. Please try again."
      );
      setMessagePopup({ open: true, message: msg, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  handleConfirmSwapRef.current = handleConfirmSwap;

  const handleKeyDown = (e) => {
    if (e.key === "Escape" || e.code === "Escape") {
      if (!submitting) {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }
      return;
    }
    if (messagePopup.open) {
      return;
    }
    const tableReady =
      filteredRows.length && !loading && !loadError && !submitting;
    const isArrowDown =
      e.key === "ArrowDown" || e.code === "ArrowDown";
    const isArrowUp = e.key === "ArrowUp" || e.code === "ArrowUp";
    const tag = (e.target && e.target.tagName) || "";

    if (e.key === "PageDown" || e.key === "PageUp") {
      if (tableReady) {
        e.preventDefault();
        e.stopPropagation();
        scrollListByPage(e.key === "PageDown" ? 1 : -1);
      }
      return;
    }

    const isShortcutPlus =
      e.key === "+" ||
      e.code === "NumpadAdd" ||
      (e.code === "Equal" && e.shiftKey);
    const isShortcutMinus =
      e.key === "-" ||
      e.code === "NumpadSubtract" ||
      e.code === "Minus";

    if (
      (isShortcutPlus || isShortcutMinus) &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      tableReady
    ) {
      e.preventDefault();
      e.stopPropagation();
      adjustSelectedQuantity(isShortcutPlus ? 1 : -1);
      return;
    }

    if (e.key === "Enter") {
      if (!tableReady) {
        return;
      }
      if (tag === "BUTTON" || tag === "TEXTAREA") {
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        adjustSelectedQuantity(-1);
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      void handleConfirmSwapRef.current();
      return;
    }

    const isHomeKey = e.key === "Home" || e.code === "Home";
    const isEndKey = e.key === "End" || e.code === "End";
    if (
      (isHomeKey || isEndKey) &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      tableReady
    ) {
      e.preventDefault();
      e.stopPropagation();
      setSelectedQuantityToBound(isHomeKey ? "home" : "end");
      return;
    }

    if (isArrowDown || isArrowUp) {
      if (tableReady) {
        e.preventDefault();
        e.stopPropagation();
        moveSelection(isArrowDown ? 1 : -1);
      }
    }
  };

  handleKeyDownRef.current = handleKeyDown;

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : handleClose}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        TransitionProps={{
          onEntering: () => {
            focusDialogShell();
          },
          onEntered: () => {
            focusFirstInteractive();
          },
        }}
        PaperProps={{
          ref: dialogPaperRef,
          tabIndex: -1,
          sx: { outline: "none" },
          "data-offer-keyboard-root": "",
          "data-offer-products-dialog": "true",
          role: "dialog",
          "aria-modal": "true",
          "aria-labelledby": "offer-products-dialog-title",
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <Typography id="offer-products-dialog-title" variant="h6" fontWeight={600}>
            Offer Products
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close dialog"
            disabled={submitting}
          >
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : loadError ? (
            <Typography sx={{ py: 2 }}>
              {loadError}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {getProductGroupIds.length > 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      gap: 1,
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                      pb: 0.5,
                      "&::-webkit-scrollbar": { height: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        borderRadius: 3,
                        backgroundColor: "rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    {getProductGroupIds.map((gid) => {
                      const idStr = String(gid);
                      const selected = idStr === String(selectedGetGroupId);
                      return (
                        <Chip
                          key={`get-group-${idStr}`}
                          onClick={() => setSelectedGetGroupId(idStr)}
                          label={`Group ${gid}`}
                          size="small"
                          color={selected ? "warning" : "default"}
                          sx={{
                            flexShrink: 0,
                            cursor: "pointer",
                            fontWeight: selected ? 700 : 500,
                            fontSize: { xs: "10px", sm: "11px" },
                            height: { xs: "26px", sm: "28px" },
                            borderRadius: "999px",
                            ...(selected
                              ? {
                                  border: "1px solid",
                                  borderColor: "warning.main",
                                  backgroundColor: "warning.lighter",
                                  color: "warning.darker",
                                }
                              : {
                                  border: "1px solid",
                                  borderColor: "#E0E0E0",
                                  backgroundColor: "common.white",
                                  color: "text.primary",
                                  "&:hover": {
                                    borderColor: "warning.light",
                                    backgroundColor: "warning.lighter",
                                  },
                                }),
                          }}
                        />
                      );
                    })}
                  </Box>
                  {selectedGroupQuantity != null && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#000",
                        fontSize: "12px",
                        lineHeight: 1.4,
                      }}
                    >
                      Select {selectedGroupQuantity} eligible product
                      {selectedGroupQuantity === 1 ? "" : "s"} from Group{" "}
                      {selectedGetGroupId}. Selected product{" "}
                      {selectedGroupFilledQty}:{selectedGroupQuantity}
                    </Typography>
                  )}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.5,
                  }}
                >
                </Box>
                <TableContainer
                  ref={tableWrapRef}
                  component={Paper}
                  className="product-picker-table-container"
                  tabIndex={-1}
                  aria-label="Offer products list"
                  sx={{
                    flex: 1,
                    maxHeight: 400,
                    overflow: "auto",
                    border: "1px solid #E0E0E0",
                    "& .MuiTableRow-root:focus": {
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  <Table
                    size="small"
                    className="table-cell-border"
                    sx={{ tableLayout: "fixed", width: "100%" }}
                  >
                    <TableHead className="table-cell-header">
                      <TableRow>
                        <TableCell
                          width="100px"
                        >
                          Code
                        </TableCell>
                        <TableCell
                          width="280px"
                        >
                          Product name
                        </TableCell>
                        <TableCell
                          align="right"
                          width="80px"
                        >
                          Stock
                        </TableCell>
                        <TableCell
                          align="center"
                          width="120px"
                        >
                          Quantity
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRows.map((row, index) => {
                        const selected = index === selectedIndex;
                        const pid = String(row.product_id);
                        const rawVal = quantities[pid] ?? 0;
                        const displayVal = Math.max(0, Math.floor(Number(rawVal) || 0));
                        const rowStaticCap = getRowStaticCap(row);
                        const promotionCap = getPromotionGetCap();
                        const totalOthers = rows.reduce((sum, r) => {
                          const rid = String(r.product_id);
                          if (rid === pid) return sum;
                          return (
                            sum +
                            Math.max(0, Math.floor(Number(quantities[rid] || 0)))
                          );
                        }, 0);
                        const dynamicCapForRow = Math.max(
                          0,
                          Math.min(rowStaticCap, promotionCap - totalOthers)
                        );
                        const inputMax = Math.max(displayVal, dynamicCapForRow);
                        const useIncrementBlocked =
                          dynamicCapForRow <= 0 || displayVal >= dynamicCapForRow;
                        const mandatoryMin = getMandatoryGetMinForRow(row);
                        const inputMin = isCompulsoryGetRow(row)
                          ? Math.max(1, mandatoryMin)
                          : 0;
                        const stockQty = resolveOfferProductStockQty(
                          row,
                          orderTypeForDisplay
                        );
                        return (
                          <TableRow
                            key={`${row.product_id}_${index}`}
                            data-row-index={index}
                            className="product-picker-table-row table-cell-style"
                            selected={selected}
                            aria-selected={selected}
                            onClick={() => {
                              setSelectedIndex(index);
                              setTimeout(() => scrollSelectedIntoView(index), 0);
                            }}
                            tabIndex={-1}
                            sx={{
                              "&:focus": {
                                outline: "none",
                              },
                              ...(selected
                                ? {
                                  bgcolor: "#ebf5ff",
                                  "&:hover": { bgcolor: "#ebf5ff" },
                                }
                                : {}),
                            }}
                          >
                            <TableCell >
                              {row.product_code || ""}
                            </TableCell>
                            <TableCell
                              title={row.product_name || ""}
                            >
                              {row.product_name || ""}
                            </TableCell>
                            <TableCell align="right">
                              {stockQty != null ? stockQty : "-"}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={(ev) => ev.stopPropagation()}
                              sx={{ py: 0.60 }}
                            >
                              <Box
                                data-offer-qty-focus=""
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                                onFocusCapture={() => setSelectedIndex(index)}
                              >
                                <NumberInput
                                  sx={{ width: 120 }}
                                  borderColor="#000"
                                  min={inputMin}
                                  max={inputMax}
                                  value={displayVal}
                                  disableInput={dynamicCapForRow <= 0 && displayVal <= 0}
                                  onIncrementBlocked={
                                    useIncrementBlocked
                                      ? showMaxQtyError
                                      : undefined
                                  }
                                  onChange={(event, value) => {
                                    const numValue =
                                      typeof value === "number"
                                        ? value
                                        : parseInt(value, 10) || 0;
                                    if (event.type === "click") {
                                      if (inputMin > 0) {
                                        if (numValue === 0 && displayVal > 0) {
                                          showCompulsoryRemoveError();
                                          return;
                                        }
                                        if (
                                          numValue > 0 &&
                                          numValue < inputMin
                                        ) {
                                          showCompulsoryMinQtyError(inputMin);
                                          return;
                                        }
                                      }
                                      let hitLimit = false;
                                      setQuantities((prev) => {
                                        const { next, reachedLimit } =
                                          applyQuantityWithRebalance(
                                            row,
                                            numValue,
                                            prev
                                          );
                                        hitLimit = reachedLimit;
                                        return next;
                                      });
                                      if (hitLimit) {
                                        showMaxQtyError();
                                      }
                                      return;
                                    }
                                    setQuantities((prev) => {
                                      const currentVal = Math.max(
                                        0,
                                        Math.floor(Number(prev[pid] ?? 0) || 0)
                                      );
                                      if (inputMin > 0) {
                                        if (numValue === 0 && currentVal > 0) {
                                          showCompulsoryRemoveError();
                                          return prev;
                                        }
                                        if (numValue > 0 && numValue < inputMin) {
                                          showCompulsoryMinQtyError(inputMin);
                                          return prev;
                                        }
                                      }
                                      const { next } = applyQuantityWithRebalance(
                                        row,
                                        Math.max(numValue, 0),
                                        prev
                                      );
                                      return next;
                                    });
                                  }}
                                  slotProps={{
                                    ...(useIncrementBlocked
                                      ? {
                                        incrementButton: {
                                          sx: {
                                            opacity: 0.45,
                                            cursor: "not-allowed",
                                          },
                                        },
                                      }
                                      : {}),
                                    input: {
                                      autoComplete: "off",
                                      "aria-label": `Quantity for ${row.product_name || row.product_code || "product"}`,
                                      onKeyDown: (ke) => {
                                        if (
                                          ke.key !== "Enter" ||
                                          ke.shiftKey ||
                                          ke.ctrlKey ||
                                          ke.metaKey
                                        ) {
                                          return;
                                        }
                                        if (messagePopup.open) {
                                          return;
                                        }
                                        if (
                                          !filteredRows.length ||
                                          loading ||
                                          loadError ||
                                          submitting
                                        ) {
                                          return;
                                        }
                                        ke.preventDefault();
                                        ke.stopPropagation();
                                        void handleConfirmSwapRef.current();
                                      },
                                    },
                                  }}
                                />
                                {(isCompulsoryGetRow(row)) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontSize="11px"
                                    sx={{ lineHeight: 1.2, whiteSpace: "nowrap" }}
                                  >
                                    Compulsory Get: {row.compulsory_quantity || 0}
                                    {mandatoryMin > (parseInt(row.compulsory_quantity || 0, 10) || 0)
                                      ? ` (min ${mandatoryMin})`
                                      : ""}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        {!loading && !loadError && rows.length > 0 ? (
          <DialogActions sx={{ px: 2, py: 1.5}}>
            <Button
              variant="outlined"
              className="button-variant-outlined"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="button-variant-contained"
              onClick={() => void handleConfirmSwapRef.current()}
              disabled={submitting}
            >
              {submitting ? "Applying…" : "Apply"}
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>
      <Dialog
        open={messagePopup.open}
        onClose={() => setMessagePopup((p) => ({ ...p, open: false }))}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {messagePopup.title ||
            (messagePopup.severity === "error"
              ? "Maximum quantity reached"
              : "Limit Reached")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            {messagePopup.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setMessagePopup((p) => ({ ...p, open: false }))}
            className="button-variant-contained"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PromotionGetProductsDialog;
